import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Calendar,
  Clock,
  MapPin,
  Users,
  Plus,
  MoreHorizontal,
  Edit,
  Trash2,
  Loader2,
} from "lucide-react";
import { useState } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { Id } from "../../../../../convex/_generated/dataModel";
import { format } from "date-fns";

interface LeadMeetingsProps {
  businessId: Id<"businesses">;
  onScheduleMeeting: () => void;
  onEditMeeting: (meeting: any) => void;
}

export function LeadMeetings({ businessId, onScheduleMeeting, onEditMeeting }: LeadMeetingsProps) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [meetingToDelete, setMeetingToDelete] = useState<any>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch events for this business
  const events = useQuery(api.calendar.getEventsByBusinessId, { businessId }) || [];

  // Delete event mutation
  const deleteEvent = useMutation(api.calendar.deleteEvent);

  // Filter for meeting-related events only
  const meetings = events.filter(
    (event: any) => event.eventType === "meeting" || event.eventType === "walkthrough"
  );

  const getEventTypeLabel = (eventType: string) => {
    switch (eventType) {
      case "walkthrough":
        return "Walkthrough";
      case "meeting":
        return "Prospect Meeting";
      default:
        return eventType;
    }
  };

  const getEventTypeColor = (eventType: string) => {
    switch (eventType) {
      case "walkthrough":
        return "bg-blue-100 text-blue-800";
      case "meeting":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatEventDateTime = (
    startDate: string,
    startTime: string,
    endTime: string,
    isAllDay: boolean
  ) => {
    // Parse the date string as local date to avoid timezone issues
    const [year, month, dayOfMonth] = startDate.split("-").map(Number);
    const date = new Date(year, month - 1, dayOfMonth); // month is 0-indexed
    const formattedDate = format(date, "MMM d, yyyy");

    if (isAllDay) {
      return `${formattedDate} (All day)`;
    }

    return `${formattedDate} at ${startTime} - ${endTime}`;
  };

  const handleDeleteMeeting = (meeting: any) => {
    setMeetingToDelete(meeting);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!meetingToDelete) return;

    setIsDeleting(true);
    try {
      await deleteEvent({ id: meetingToDelete._id });
      setDeleteDialogOpen(false);
      setMeetingToDelete(null);
    } catch (error) {
      console.error("Error deleting meeting:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  if (meetings.length === 0) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Meetings</h3>
          <Button onClick={onScheduleMeeting} className="gap-2">
            <Plus className="h-4 w-4" />
            Schedule Meeting
          </Button>
        </div>
        <EmptyState
          icon={Calendar}
          title="No meetings scheduled"
          description="Schedule a walkthrough or prospect meeting to get started."
        >
          <Button onClick={onScheduleMeeting} className="mt-4 gap-2">
            <Plus className="h-4 w-4" />
            Schedule Meeting
          </Button>
        </EmptyState>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Meetings ({meetings.length})</h3>
        <Button onClick={onScheduleMeeting} className="gap-2">
          <Plus className="h-4 w-4" />
          Schedule Meeting
        </Button>
      </div>

      <div className="space-y-3">
        {meetings.map((meeting: any) => (
          <Card key={meeting._id} className="p-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="mb-2 flex items-center gap-2">
                  <h4 className="font-medium">{meeting.title}</h4>
                  <Badge className={getEventTypeColor(meeting.eventType)}>
                    {getEventTypeLabel(meeting.eventType)}
                  </Badge>
                </div>

                {meeting.description && (
                  <p className="mb-3 text-sm text-gray-600">{meeting.description}</p>
                )}

                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Calendar className="h-4 w-4" />
                    <span>
                      {formatEventDateTime(
                        meeting.startDate,
                        meeting.startTime,
                        meeting.endTime,
                        meeting.isAllDay
                      )}
                    </span>
                  </div>

                  {(meeting.location || meeting.address) && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <MapPin className="h-4 w-4" />
                      <span>
                        {meeting.location && meeting.address
                          ? `${meeting.location}, ${meeting.address}`
                          : meeting.location || meeting.address}
                      </span>
                    </div>
                  )}

                  {meeting.assignedStaffDetails && meeting.assignedStaffDetails.length > 0 && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Users className="h-4 w-4" />
                      <span>Assigned to: {meeting.assignedStaffDetails.join(", ")}</span>
                    </div>
                  )}
                </div>

                <div className="mt-3 text-xs text-gray-500">Created by {meeting.createdByName}</div>
              </div>

              <div className="flex items-center gap-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => onEditMeeting(meeting)}>
                      <Edit className="mr-2 h-4 w-4" />
                      Edit Meeting
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => handleDeleteMeeting(meeting)}
                      className="text-red-600"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete Meeting
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you want to delete?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete "{meetingToDelete?.title}"
              and remove it from our servers.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
